import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie } from 'lucide-react';
import { useLang } from '../i18n/LangContext';

const CONSENT_KEY = 'krug-cookie-consent';

export const CookieBanner = () => {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(CONSENT_KEY)) {
      const id = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(id);
    }
    return undefined;
  }, []);

  useEffect(() => {
    const reopen = () => setVisible(true);
    window.addEventListener('krug-cookie-settings', reopen);
    return () => window.removeEventListener('krug-cookie-settings', reopen);
  }, []);

  const choose = (value) => {
    localStorage.setItem(CONSENT_KEY, value);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="cookie-banner"
          role="dialog"
          aria-label="Cookie saglasnost"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-5 left-5 right-5 sm:right-auto sm:max-w-md z-[70] rounded-3xl glass-dark bg-krug-surface/95 p-6 shadow-[0_24px_70px_-20px_rgba(0,0,0,0.8)]"
        >
          <div className="flex items-start gap-4">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-krug-gold/15 text-krug-goldbright shrink-0">
              <Cookie size={20} />
            </span>
            <div>
              <p className="text-sm text-slate-300 leading-relaxed">{t.footer.cookie.text}</p>
              <Link
                to="/privacy"
                data-testid="cookie-privacy-link"
                className="mt-1.5 inline-block text-xs font-bold text-krug-tealbright hover:underline"
              >
                {t.footer.cookie.more}
              </Link>
            </div>
          </div>
          <div className="mt-5 flex gap-3">
            <button
              type="button"
              data-testid="cookie-accept"
              onClick={() => choose('all')}
              className="flex-1 rounded-full bg-krug-gold px-5 py-2.5 text-sm font-bold text-krug-navy hover:bg-krug-goldbright transition-colors duration-300"
            >
              {t.footer.cookie.accept}
            </button>
            <button
              type="button"
              data-testid="cookie-decline"
              onClick={() => choose('necessary')}
              className="flex-1 rounded-full border border-white/20 px-5 py-2.5 text-sm font-bold text-white hover:border-krug-tealbright hover:text-krug-tealbright transition-colors duration-300"
            >
              {t.footer.cookie.decline}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
